import React, { Component } from 'react';
import PropTypes from 'prop-types';
import compact from 'lodash/compact';
import find from 'lodash/find';
import get from 'lodash/get';
import head from 'lodash/head';
import isString from 'lodash/isString';
import isArray from 'lodash/isArray';
import tail from 'lodash/tail';
import { format as currencyFormat } from 'currency-formatter';
import ModalPortal from '../ModalPortal';
import * as analytics from '../../services/analytics';
import logo from './chubb-logo-dark.svg';
import './BOAEnrollmentPlansView.css';

const propTypes = {
  client: PropTypes.object,
  plans: PropTypes.array,
  quote: PropTypes.object,
  onSubmit: PropTypes.func,
  updatePlansFilter: PropTypes.func,
};

class BOAEnrollmentPlansView extends Component {
  constructor(props) {
    super(props);
    const region = get(props.quote, 'application.rating_address.region.code');
    this.coverageKeys = {
      owner: {
        categoryKey: 'business_owner_coverage',
        detailKeys: [
          'business_owner_accidental_death_and_dismemberment',
          'business_owner_paralysis',
          'business_owner_cobra_premium_expense',
          region === 'VT' ? null : 'business_owner_permanent_total_disability',
          region === 'MO' ? null : 'business_owner_temporary_total_disability',
          region === 'NY' ? null : 'business_owner_enhanced_in_hospital',
        ],
      },
      employee: {
        categoryKey: 'employee_coverage',
        detailKeys: [
          'employee_accidental_death_and_dismemberment_assault',
          'employee_accidental_death_and_dismemberment_business',
          'employee_paralysis',
          'employee_cobra_premium_expense',
          region === 'VT' ? null : 'employee_permanent_total_disability',
          region === 'MO' ? null : 'employee_temporary_total_disability',
          region === 'NY' ? null : 'employee_enhanced_in_hospital',
        ],
      },
    };
    this.state = {
      modalTitle: null,
      modalBody: null,
    };
  }

  componentDidMount() {
    analytics.page('BOA - Enrollment Plans');
    this.props.getPlans(this.props.quote.quote_number);
  }

  componentWillUnmount() {
    window.$(this.modal).modal('hide');
  }

  handleNext = (e) => {
    this.props.updatePlansFilter({
      planName: e.target.dataset.planName,
      planType: e.target.dataset.planType,
    });
    this.props.onSubmit();
  };

  getCellText = (items) => {
    return items.map((item, i) => {
      if (i + 1 < items.length) {
        return `${item}\n`;
      } else {
        return item;
      }
    });
  };

  showInfoModal = (e) => {
    analytics.page('BOA - Coverage Details Modal Opened', {
      title: e.currentTarget.dataset.modalTitle,
    });
    this.setState(
      {
        modalTitle: e.currentTarget.dataset.modalTitle,
        modalBody: JSON.parse(e.currentTarget.dataset.modalBody),
      },
      () => {
        window.$(this.modal).modal('show');
      }
    );
  };

  trackDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.key,
    });
  };

  renderCoverageDetails = (category, subcategory) => {
    const coverageDetailKeys = this.coverageKeys[category].detailKeys;
    const categoryKey = this.coverageKeys[category].categoryKey;
    let subcategoryKey;
    switch (subcategory) {
      case 'primary':
        subcategoryKey = 'primary_benefits';
        break;
      case 'additional':
        subcategoryKey = 'additional_benefits';
        break;
      default:
        break;
    }

    const coverageDetails = get(
      this.plan,
      `settings.details.coverage_details.${categoryKey}.${subcategoryKey}`
    );

    if (coverageDetails) {
      return compact(
        coverageDetailKeys.map((key) => {
          const coverageDetail = get(coverageDetails, key);
          if (coverageDetail) {
            const title = get(coverageDetail, 'title');
            const description = get(coverageDetail, 'description');
            return (
              <tr key={key}>
                <td>
                  {title}{' '}
                  {description && (
                    <span
                      data-modal-title={title}
                      data-modal-body={JSON.stringify(description)}
                      onClick={this.showInfoModal}
                    >
                      <i className="PolicyDetailsView-infoIcon fas fa-info-circle" />
                    </span>
                  )}
                </td>
                <td>{this.getCellText(coverageDetail.coverage_details)}</td>
                <td>{this.getCellText(coverageDetail.value_details)}</td>
              </tr>
            );
          } else {
            return null;
          }
        })
      );
    } else {
      return null;
    }
  };

  coverageTable = (category) => (
    <table className="BOAEnrollmentPlansView-table table table-striped">
      <thead>
        <tr>
          <th>Primary Benefits</th>
          <th>Hazard for which coverage applies</th>
          <th>Benefit amount</th>
        </tr>
      </thead>
      <tbody>{this.renderCoverageDetails(category, 'primary')}</tbody>
      <thead>
        <tr>
          <th>Additional Benefits</th>
          <th>Hazard for which coverage applies</th>
          <th>Benefit amount</th>
        </tr>
      </thead>
      <tbody>{this.renderCoverageDetails(category, 'additional')}</tbody>
    </table>
  );

  get plan() {
    return find(this.props.plans, {
      settings: {
        details: {
          plan_name: 'boa',
        },
      },
    });
  }

  get title() {
    const { actor, client } = this.props;
    if (client.person.company_name) {
      return `Quote for ${client.person.company_name}`;
    } else if (actor === 'broker') {
      return `Quote for ${client.person.first_name}'s company`;
    } else {
      return `Quote for your company`;
    }
  }

  get modalBody() {
    if (isString(this.state.modalBody)) {
      return <div className="modal-body">{this.state.modalBody}</div>;
    } else if (isArray(this.state.modalBody)) {
      const tableHeaders = head(this.state.modalBody);
      const tableValues = tail(this.state.modalBody);
      return (
        <table className="table modal-table table-striped BOAEnrollmentPlansView-modalTable">
          <thead>
            <tr>
              <th>{tableHeaders.title}</th>
              <th>{tableHeaders.value}</th>
            </tr>
          </thead>
          <tbody>
            {tableValues.map((item) => (
              <tr key={item.title}>
                <td>{item.title}</td>
                <td>{item.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } else {
      return null;
    }
  }

  render() {
    return (
      <>
        <div className="row justify-content-center">
          <div className="col-md-12 p-t-30">
            <h3 className="text-center semi-bold">{this.title}</h3>
            <div className="BOAEnrollmentPlansView-plans card m-t-20">
              <div className="BOAEnrollmentPlansView-plansHeader">
                <div className="BOAEnrollmentPlansView-plansHeader-col BOAEnrollmentPlansView-plansHeader-underwriter">
                  <p className="text-uppercase semi-bold">
                    All policies issued by
                  </p>
                  <img src={logo} alt="Chubb logo" />
                </div>
                {this.plan && (
                  <div className="BOAEnrollmentPlansView-plansHeader-col BOAEnrollmentPlansView-plansHeader-boa">
                    <h4 className="BOAEnrollmentPlansView-plansHeader-title">
                      {get(this.plan, 'settings.plan_title')}
                    </h4>
                    <p className="BOAEnrollmentPlansView-plansHeader-price">
                      {currencyFormat(get(this.plan, 'settings.price'), {
                        code: get(this.plan, 'currency'),
                        precision: 2,
                      })}
                    </p>
                    <button
                      className="btn btn-large btn-complete m-b-10"
                      data-plan-name="boa"
                      data-plan-type="chubb_boa"
                      onClick={this.handleNext}
                    >
                      Select plan
                    </button>
                    <a
                      href={get(this.plan, 'settings.sample_policy_url')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="BOAEnrollmentPlansView-plansHeader-policyDocLink"
                      data-key="boaPlan"
                      onClick={this.trackDocClick}
                    >
                      <i className="BOAEnrollmentPlansView-plansHeader-policyDocLink-icon fal fa-file-alt" />
                      view sample policy
                    </a>
                  </div>
                )}
              </div>
              <div className="BOAEnrollmentPlansView-tableHeading">
                For Business Owners
              </div>
              {this.coverageTable('owner')}
              <div className="BOAEnrollmentPlansView-tableHeading">
                For Employees
              </div>
              {this.coverageTable('employee')}
            </div>
          </div>
        </div>

        <ModalPortal>
          <div className="modal" id="infoModal" ref={(c) => (this.modal = c)}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content-wrapper">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{this.state.modalTitle}</h5>
                    <button
                      type="button"
                      className="close"
                      data-dismiss="modal"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                  {this.modalBody}
                  <div className="modal-footer">
                    <button
                      type="submit"
                      className="btn btn-complete"
                      data-dismiss="modal"
                    >
                      Ok
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      </>
    );
  }
}

BOAEnrollmentPlansView.propTypes = propTypes;

export default BOAEnrollmentPlansView;
